'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { useStore } from 'zustand';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { AskAddMoreExams } from './_variants/ask-add-more-exams.component';
import { ExamsCoverage } from './_variants/exams-coverage.component';
import { SearchModalDefault } from './_variants/search-modal-default.component';
import { SelectMoreExams } from './_variants/select-more-exams.component';
import { SelectedOverviewExams } from './_variants/selected-overview-exams.component';
import { ProgressiveSearchModalKeys, RecentSearches, SearchModalProps, SearchResult, SelectedProcedures } from './search-modal.types';
import { BackNavigationResult, resolveBackNavigation } from './_utils/resolve-back-navigation.util';

import { TrackUserView } from '@/app/(main)/_components/track-user-view/track-user-view.component';
import LoadRecentSearchesComponent from '@/app/(main)/(pages)/(autoload)/load-recent-searches.component';
import { ProgressiveModal } from '@/components/ui/progressive-modal/progressive-modal.component';
import { ProgressiveModalEventHandlerProps } from '@/components/ui/progressive-modal/progressive-modal.types';
import { clearExamsCoverage, clearPendingModalSeed, openSearchModal, useSearchState } from '@/store/search/search.store';
import { MostSearchedResult } from '@/types/most-search-type';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';

function applyBackNavigation(
  result: BackNavigationResult,
  handleJump: ProgressiveModalEventHandlerProps['handleJump'],
): void {
  if (!result) {
    return;
  }

  if (result.action === 'close') {
    openSearchModal(false);
    return;
  }

  if (result.action === 'jump') {
    handleJump({ key: result.key, returning: result.returning });
  }
}

export function SearchModal({
  initialSelectedProcedures,
  initialKey,
  locationOverride,
}: SearchModalProps) {
  const { isSearchModalOpen, pendingModalKey, pendingSelectedProcedures } = useStore(
    useSearchState,
    (state) => ({
      isSearchModalOpen: state?.isSearchModalOpen,
      pendingModalKey: state?.pendingModalKey,
      pendingSelectedProcedures: state?.pendingSelectedProcedures,
    }),
  );

  const [key, setKey] = useState(initialKey ?? ProgressiveSearchModalKeys.SEARCH);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [selectedProcedures, setSelectProcedures] = useState<SelectedProcedures[]>(initialSelectedProcedures || []);
  const [firstProcedureSelected, setFirstProcedureSelected] = useState<SelectedProcedures>({} as SelectedProcedures);
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (isSearchModalOpen) {
      openSearchModal(false);
    }

    setKey(ProgressiveSearchModalKeys.SEARCH);
    onClear();
    clearExamsCoverage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, searchParams]);

  useEffect(() => {
    if (!pendingModalKey) {
      return;
    }

    setKey(pendingModalKey);
    setSelectProcedures(pendingSelectedProcedures ?? []);
    clearPendingModalSeed();
  }, [pendingModalKey, pendingSelectedProcedures]);

  function handleSelectProcedure(
    procedure: SelectedProcedures | MostSearchedResult | SearchResult | RecentSearches,
  ): void {
    if (key === ProgressiveSearchModalKeys.SEARCH) {
      onClear();
    }

    if (selectedProcedures.some((item) => item.id === procedure.id)) {
      setSelectProcedures((prev) => prev.filter((item) => item.id !== procedure.id));
      return;
    }

    if (!firstProcedureSelected.id) {
      setFirstProcedureSelected(procedure as SelectedProcedures);
    }

    if ((procedure as RecentSearches).examOrVaccineGroup) {
      const newProcedures = (procedure as RecentSearches).examOrVaccineGroup?.map((item) => ({
        id: item.id!,
        code: item.code!,
        displayName: item.displayName!,
      })) as SelectedProcedures[];

      const uniqueProcedures = newProcedures.filter(
        (newProcedure) => !selectedProcedures.some((selected) => selected.id === newProcedure.id),
      );

      setSelectProcedures((prev) => [...prev, ...uniqueProcedures]);
      return;
    }

    setSelectProcedures((prev) => [
      ...prev,
      {
        id: procedure.id,
        code: procedure.code,
        displayName: procedure.displayName,
      } as SelectedProcedures,
    ]);
  }

  function onClear() {
    setSelectProcedures([]);
    setFirstProcedureSelected({} as SelectedProcedures);
  }

  function onEscapeKeyDown({ event, currentKey, handleJump }: ProgressiveModalEventHandlerProps) {
    const result = resolveBackNavigation(currentKey, 'escape');
    if (!result) {
      return;
    }

    event.preventDefault();
    applyBackNavigation(result, handleJump);
  }

  function onInteractOutside({ event, currentKey, handleJump }: ProgressiveModalEventHandlerProps) {
    event.preventDefault();
    applyBackNavigation(resolveBackNavigation(currentKey, 'interact-outside'), handleJump);
  }

  function onOpenAutoFocus({ event, currentKey }: ProgressiveModalEventHandlerProps) {
    event.preventDefault();

    const { openContext } = useSearchState.getState();
    const opensEmptySearch = currentKey === ProgressiveSearchModalKeys.SEARCH
      && !openContext?.term
      && !openContext?.initialStep;

    if (opensEmptySearch && searchInputRef.current) {
      searchInputRef.current.focus();
      return;
    }

    (event.target as HTMLElement | null)?.focus();
  }

  return (
    <>
      <LoadRecentSearchesComponent />
      {isSearchModalOpen && <TrackUserView eventName={AnalyticsEnum.VIEW_SEARCH_MODAL_DEFAULT} />}

      <ProgressiveModal
        data-testid={DataTestIdEnum.SEARCH_MODAL}
        className="h-full lg:h-[calc(4/6*100vh)]"
        sheetSide="centerLarge"
        open={isSearchModalOpen}
        hasHeader
        title="Modal de busca"
        description="Busque por procedimentos médicos, especialidades ou profissionais de saúde"
        initialKey={key}
        contentPadding={false}
        handleClose={() => openSearchModal(false)}
        onInteractOutside={onInteractOutside}
        onEscapeKeyDown={onEscapeKeyDown}
        onOpenAutoFocus={onOpenAutoFocus}
        onCloseAutoFocus={({ event }) => event.preventDefault()}
        onKeyChange={setKey}
      >
        <SearchModalDefault
          key={ProgressiveSearchModalKeys.SEARCH}
          setOpen={openSearchModal}
          selectLaboratoryProcedure={handleSelectProcedure}
          locationOverride={locationOverride}
          inputRef={searchInputRef}
        />

        <AskAddMoreExams
          key={ProgressiveSearchModalKeys.ASK_ADD_MORE_EXAMS}
          selectedProcedures={selectedProcedures}
          locationOverride={locationOverride}
        />

        <SelectMoreExams
          key={ProgressiveSearchModalKeys.SELECT_MORE_EXAMS}
          selectedProcedures={selectedProcedures}
          locationOverride={locationOverride}
          onClear={onClear}
          setSelectProcedures={setSelectProcedures}
        />

        <SelectedOverviewExams
          key={ProgressiveSearchModalKeys.SELECTED_OVERVIEW_EXAMS}
          selectedProcedures={selectedProcedures}
          handleSelectProcedure={handleSelectProcedure}
          locationOverride={locationOverride}
          onClear={onClear}
        />

        <ExamsCoverage
          key={ProgressiveSearchModalKeys.EXAMS_COVERAGE}
          onSeeSchedules={() => openSearchModal(false)}
        />
      </ProgressiveModal>
    </>
  );
}
